// Merges the two dictionary halves into one lookup keyed by segment id, and
// exposes the three functions the parser/validator/UI use to explain a
// segment, an element, or a coded value. Every lookup has a defined fallback
// — nothing here returns `undefined`, so a caller never needs to branch on
// "not found" just to render something.

import {HEADER_SEGMENTS} from './header-segments';
import {ITEM_LOOP_SEGMENTS} from './item-loop-segments';
import {HEADER_SEGMENTS_852} from './852-header-segments';
import {ITEM_ACTIVITY_SEGMENTS_852} from './852-item-activity-segments';
import {SEGMENTS_856} from './856-segments';
import {HEADER_SEGMENTS_855} from './855-header-segments';
import {ACK_SEGMENTS_855} from './855-ack-segments';
import {SEGMENTS_997} from './997-segments';
import {SEGMENTS_840} from './840-segments';
import {SEGMENTS_843} from './843-segments';
import {SEGMENTS_810} from './810-segments';
import {SEGMENTS_844} from './844-segments';
import {SEGMENTS_845} from './845-segments';
import {SEGMENTS_867} from './867-segments';
import {SEGMENTS_210} from './210-segments';
import {
  unknownElementDefinition,
  unknownSegmentDefinition,
  type ElementDefinition,
  type SegmentDefinition,
} from './types';

const SEGMENT_DEFINITIONS: Record<string, SegmentDefinition> = {
  ...HEADER_SEGMENTS,
  ...ITEM_LOOP_SEGMENTS,
  ...HEADER_SEGMENTS_852,
  ...ITEM_ACTIVITY_SEGMENTS_852,
  ...SEGMENTS_856,
  ...HEADER_SEGMENTS_855,
  ...ACK_SEGMENTS_855,
  ...SEGMENTS_997,
  ...SEGMENTS_840,
  ...SEGMENTS_843,
  ...SEGMENTS_810,
  ...SEGMENTS_844,
  ...SEGMENTS_845,
  ...SEGMENTS_867,
  ...SEGMENTS_210,
};

export function getSegmentDefinition(segmentId: string): SegmentDefinition {
  return SEGMENT_DEFINITIONS[segmentId] ?? unknownSegmentDefinition(segmentId);
}

export function getElementDefinition(
  segmentId: string,
  position: number
): ElementDefinition {
  const found = SEGMENT_DEFINITIONS[segmentId]?.elements.find(
    e => e.position === position
  );
  return found ?? unknownElementDefinition(segmentId, position);
}

/** Resolves a raw coded value to its plain-English label. Falls back to the
 * raw code itself when the segment/position isn't in the dictionary or the
 * code isn't in that element's list — the caller always gets a string to
 * display, never `undefined`. The match against the code list trims
 * incidental surrounding whitespace first (matching validator.ts's
 * tolerance) and case-folds (so a lowercase/mixed-case value like 'zz'
 * still finds the list's 'ZZ' entry, keeping this in agreement with
 * validator.ts's case-insensitive ZZ exemption), so a padded-but-known code
 * still renders its label rather than falling back as if unrecognized; the
 * fallback returns the original, untrimmed value. */
export function getCodeLabel(
  segmentId: string,
  position: number,
  code: string
): string {
  const element = getElementDefinition(segmentId, position);
  const trimmedCode = code.trim().toUpperCase();
  return (
    element.codes?.find(c => c.code.toUpperCase() === trimmedCode)?.label ??
    code
  );
}

export {LoopId} from './types';
export type {
  CodeListEntry,
  ElementDefinition,
  ElementDataType,
  SegmentDefinition,
} from './types';
